import { McpAppCard } from '@/components/chat/Messages/Message/McpAppCard';
import { cn } from '@/lib/utils';

import type { PanelContentProps } from '../types';

function HtmlPreviewCard({
    html,
    filename
}: {
    html: string;
    filename?: string;
}) {
    // Fill the available sidebar height via flexbox (instead of a fixed vh
    // height) so the iframe is exactly the visible panel height. This keeps the
    // iframe's own scrollbar covering the whole dashboard and reachable — a fixed
    // height taller than the panel would push the bottom off-screen and the
    // iframe would trap the scroll wheel.
    return (
        <div className="flex flex-col gap-2 h-full min-h-0">
            <div className="flex justify-between items-center px-1 shrink-0">
                <span className="font-mono text-muted-foreground text-xs">
                    {filename || 'HTML Preview'}
                </span>
            </div>
            <div className="border border-border rounded-lg overflow-hidden flex-1 min-h-0">
                <iframe
                    srcDoc={html}
                    sandbox="allow-scripts allow-same-origin allow-downloads"
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        background: '#fff'
                    }}
                    title={filename || 'HTML Preview'}
                />
            </div>
        </div>
    );
}

export function AppsPanel({ elements }: PanelContentProps) {
    if (elements.length === 0) {
        return (
            <div className="flex flex-col justify-center items-center gap-y-1 py-8 text-center">
                <p className="text-secondary-foreground text-base">
                    No charts available
                </p>
                <p className="text-muted-foreground text-xs">
                    Charts returned by MCP tools will appear here
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-8 h-full">
            {elements.map((el) => {
                const props = (el as any).props ?? {};
                const name = (el as any).name;
                const isHtml = name === 'HtmlPreview';
                return (
                    <div
                        key={el.id}
                        data-app-element-id={el.id}
                        className={cn(
                            'rounded-lg [&.chart-highlight]:ring-2 [&.chart-highlight]:ring-foreground/20 transition-all duration-500',
                            isHtml && 'flex-1 min-h-0 flex flex-col'
                        )}
                    >
                        {isHtml ? (
                            <HtmlPreviewCard
                                html={props.html}
                                filename={props.filename}
                            />
                        ) : (
                            <McpAppCard
                                className="my-0"
                                mcpAppMetadata={{
                                    resourceUri: props.resourceUri,
                                    serverUrl: props.serverUrl,
                                    toolName: props.toolName,
                                    toolResult: props.toolResult,
                                    csp: props.csp,
                                    permissions: props.permissions
                                }}
                                height={
                                    props.toolName === 'render_map'
                                        ? 'calc(100vh - 160px)'
                                        : undefined
                                }
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
